"use client"

import { useState } from "react"
import { motion } from "motion/react"
import { ArrowRight, CheckCircle2 } from "lucide-react"

export function NewsletterElite() {
  const [isHovered, setIsHovered] = useState(false)
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [email, setEmail] = useState("")

  // Mock past editions for the archive fan
  const archives = [
    { title: "Zero-Downtime DB Migrations", date: "Vol. 42", color: "bg-premium-cyan" },
    { title: "Building Agentic Pipelines", date: "Vol. 41", color: "bg-premium-violet" },
    { title: "The Truth About Microservices", date: "Vol. 40", color: "bg-slate-700" },
  ]

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault()
    if (email) setIsSubscribed(true)
  }

  return (
    <section className="container max-w-5xl mx-auto px-4 py-24 relative z-20">
      
      {/* 
        The Tactile Container: Digital Obsidian
        Mimics a heavy, tangible slab of anodized dark metal rather than flat blur.
      */}
      <div 
        className="relative rounded-[2.5rem] bg-[#050505] border border-white/5 p-8 md:p-12 lg:p-16 overflow-hidden flex flex-col lg:flex-row gap-12 lg:gap-8 items-center shadow-[0_40px_100px_rgba(0,0,0,0.8),inset_0_1px_0_rgba(255,255,255,0.1)]"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        
        {/* Subtle Ambient Rim Glow */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-premium-cyan/20 to-transparent" />
        
        {/* Left Column: Copy & Input */}
        <div className="flex-1 w-full relative z-20 flex flex-col justify-center">
          
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded bg-white/[0.03] border border-white/5 mb-6 w-max shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
            <div className="w-1.5 h-1.5 rounded-full bg-premium-cyan animate-pulse shadow-[0_0_8px_rgba(8,145,178,0.8)]" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 font-mono">
              The Architecture Log
            </span>
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-[family-name:var(--font-display)] font-semibold text-white mb-4 tracking-tight leading-[1.1] drop-shadow-lg">
            High-Signal.<br />
            <span className="text-slate-500">Zero Spam.</span>
          </h2>

          <p className="text-lg text-slate-400 mb-10 max-w-md font-light leading-relaxed">
            Join 8,000+ senior engineers receiving one deep-dive architectural teardown every Sunday.
          </p>

          {/* Frictionless, Tactile Input */}
          {!isSubscribed ? (
            <form onSubmit={handleSubscribe} className="relative w-full max-w-md group">
              <div className="absolute -inset-1 rounded-[2rem] bg-gradient-to-r from-premium-cyan/20 to-premium-violet/20 blur-lg opacity-0 transition-opacity duration-500 group-focus-within:opacity-100" />
              <div className="relative flex items-center bg-[#0a0a0a] rounded-full p-2 border border-white/10 shadow-[inset_0_2px_4px_rgba(0,0,0,0.4)] transition-colors group-focus-within:border-premium-cyan/50">
                <input
                  type="email"
                  required
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-transparent border-none text-white px-6 focus:outline-none placeholder:text-slate-600 font-light"
                />
                
                {/* Physics-Bouncing Action Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 25 }}
                  type="submit"
                  className="flex items-center justify-center gap-2 bg-white text-black px-6 py-3.5 rounded-full font-semibold text-sm hover:bg-slate-200 transition-colors shadow-lg"
                >
                  Join <ArrowRight className="w-4 h-4" />
                </motion.button>
              </div>
            </form>
          ) : (
            <motion.div 
               initial={{ opacity: 0, y: 10 }}
               animate={{ opacity: 1, y: 0 }}
               className="flex items-center gap-3 p-4 rounded-full bg-premium-cyan/10 border border-premium-cyan/20 w-max"
            >
              <CheckCircle2 className="w-5 h-5 text-premium-cyan" />
              <span className="text-white font-medium text-sm">Clearance granted. Welcome aboard.</span>
            </motion.div>
          )}

          {/* Social Proof Footer */}
          <div className="mt-8 flex items-center gap-4 text-[11px] font-mono text-slate-500 uppercase tracking-widest">
            <div className="flex -space-x-2">
              <div className="w-6 h-6 rounded-full bg-slate-800 border-2 border-[#050505] shadow-sm" />
              <div className="w-6 h-6 rounded-full bg-slate-700 border-2 border-[#050505] shadow-sm" />
              <div className="w-6 h-6 rounded-full bg-premium-cyan/20 border-2 border-[#050505] shadow-sm" />
            </div>
            <span>Read by engineers at top-tier SaaS</span>
          </div>
        </div>

        {/* Right Column: Immersive Interactive Fan */}
        <div className="hidden lg:flex flex-1 w-full relative h-[300px] items-center justify-center perspective-[1000px]">
          <div className="relative w-[280px] h-[360px] transform-style-3d">
            {archives.map((archive, index) => {
              // Mathematical Fan Spread
              const rotation = isHovered ? (index - 1) * 15 : 0;
              const xOffset = isHovered ? (index - 1) * 40 : 0;
              const yOffset = isHovered ? Math.abs(index - 1) * 15 : 0;
              
              return (
                <motion.div
                  key={index}
                  initial={false}
                  animate={{
                    rotateZ: rotation,
                    x: xOffset,
                    y: yOffset,
                    scale: 1 - Math.abs(index - 1) * 0.05
                  }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-80 rounded-2xl bg-[#0B0E14] border border-white/10 shadow-2xl overflow-hidden flex flex-col origin-bottom cursor-pointer transform-gpu z-${30 - index * 10}`}
                >
                  {/* Card Cover Art */}
                  <div className={`h-1/2 w-full ${archive.color} bg-opacity-20 flex items-center justify-center relative overflow-hidden`}>
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14] to-transparent" />
                    <span className="font-mono text-white/20 text-4xl font-bold tracking-tighter mix-blend-overlay">
                      {archive.date}
                    </span>
                  </div>
                  {/* Card Content */}
                  <div className="flex-1 p-5 flex flex-col border-t border-white/5 bg-[#0B0E14]">
                    <span className="text-[10px] font-mono text-slate-500 mb-2">ARCHIVE PREVIEW</span>
                    <h4 className="text-white font-[family-name:var(--font-display)] font-semibold leading-tight text-lg drop-shadow-sm">
                      {archive.title}
                    </h4>
                    <div className="mt-auto flex justify-between items-center text-slate-600">
                      <div className="flex gap-1">
                        <div className="w-1.5 h-1.5 rounded-full bg-white/10" />
                        <div className="w-1.5 h-1.5 rounded-full bg-white/10" />
                      </div>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>

      </div>
    </section>
  )
}
